import { useState } from 'react'
import { FileText, Film, Image as ImageIcon, Music, type LucideIcon } from 'lucide-react'
import { formatBytes } from '@/utils/formatBytes'
import { canShow, viewableKind, type ViewableKind } from '@/utils/mediaView'
import { MediaViewerDialog, type ViewerItem, type ViewerSet } from './MediaViewerDialog'
import { INLINE_VIDEO_MAX_BYTES, InlineVideo } from './InlineVideo'
import { MediaDownloadButton } from './MediaDownloadButton'
import { kindLabel, MEDIA_TEXT, thumbnailLabel } from './mediaViewText'
import { useMediaBlob } from './mediaBlob'
import { INLINE_IMAGE_MAX_BYTES, loadsUpFrontAsImage } from './mediaGallery'

interface MediaAttachmentProps {
  messageId: string
  mediaType: string
  mediaUrl: string
  /** SA-715: the sender's filename (an email attachment) — the document
   * link's text and download name. Absent → the generic label (WhatsApp). */
  filename?: string | null
  /** SA-979: the stored size (`channel_metadata.byte_size`), when known. It
   * decides whether an image or audio loads up front, and is shown beside a
   * download. */
  byteSize?: number | null
  /** SA-981: the stored MIME type (`channel_metadata.mime_type`). It decides
   * whether a click shows the file in the viewer; absent → as before. */
  mimeType?: string | null
  /** SA-1094: the message's viewable attachments and this one's place in
   * them, so the viewer can step. Absent → a set of one. */
  viewer?: ViewerSet
}

type WithKind<K> = MediaAttachmentProps & { kind: K }

/** SA-979 (design note §4.1): the largest image and audio that still load
 * up front, from a blob. Everything else is fetched only on click. */
export { INLINE_IMAGE_MAX_BYTES }
export const INLINE_AUDIO_MAX_BYTES = 25 * 1024 * 1024

const KIND_ICON: Record<ViewableKind, LucideIcon> = {
  pdf: FileText,
  image: ImageIcon,
  video: Film,
  audio: Music,
}

/** The set the dialog opens on: the bubble's, with this attachment's own
 * bytes merged into its item, else a set of one. SA-1110: a set whose item
 * at `index` is another file is not trusted; it opens a set of one. */
function viewerFor(
  props: MediaAttachmentProps, kind: ViewableKind, objectUrl: string | null,
): { items: ViewerItem[]; index: number } {
  const own: ViewerItem = {
    kind, messageId: props.messageId, mediaUrl: props.mediaUrl,
    filename: props.filename, byteSize: props.byteSize, objectUrl,
  }
  const set = props.viewer
  if (!set || set.items[set.index]?.messageId !== props.messageId) return { items: [own], index: 0 }
  return { items: set.items.map((it, i) => (i === set.index ? { ...it, objectUrl } : it)), index: set.index }
}

/** SA-1110 (design note R2): what loads up front from a blob, by the one
 * rule the grid reads too; audio keeps its own cap. */
function upFront({ mediaType, mimeType, byteSize }: MediaAttachmentProps): 'image' | 'audio' | null {
  if (loadsUpFrontAsImage({ mediaType, mimeType, byteSize })) return 'image'
  if (mediaType === 'audio' && (byteSize == null || byteSize <= INLINE_AUDIO_MAX_BYTES)) return 'audio'
  return null
}

/**
 * Renders one stored attachment (inbound media or an outbound AI voice note).
 * A small image (by `mediaGallery.loadsUpFrontAsImage`) or voice note loads up front (SA-979, SA-1110). A video up to the cap
 * plays in the bubble (SA-1094). A file the browser can show opens the viewer
 * on click (SA-981). Anything else is a download
 * button, so opening a thread never pulls a large file.
 */
export function MediaAttachment(props: MediaAttachmentProps) {
  const kind = viewableKind(props.mimeType)
  const front = upFront(props)
  if (front) {
    return <InlineMedia {...props} kind={kind} show={front} />
  }
  if (kind === 'video' && (props.byteSize == null || props.byteSize <= INLINE_VIDEO_MAX_BYTES)) {
    // SA-1094: a video plays in the bubble up to the cap (ruling M1).
    return <InlineVideo messageId={props.messageId} mediaUrl={props.mediaUrl} filename={props.filename} byteSize={props.byteSize} />
  }
  if (kind && canShow(kind, props.mimeType)) {
    return <ViewButton {...props} kind={kind} />
  }
  return <MediaDownloadButton {...props} />
}

/**
 * The media route is Bearer-auth-gated, so a bare <img src> would 401 —
 * fetch via apiClient (JWT header) and render from an object URL instead.
 * SA-981: a typed raster thumbnail is a button that opens the viewer on the
 * same bytes.
 */
function InlineMedia(props: WithKind<ViewableKind | null> & { show: 'image' | 'audio' }) {
  const { kind, show, mediaUrl, filename } = props
  const { objectUrl, gone, error } = useMediaBlob(mediaUrl)
  const [viewing, setViewing] = useState(false)

  if (gone) {
    return (
      <p className="text-xs text-muted-foreground italic mb-1">
        {show === 'audio' ? MEDIA_TEXT.voiceNoteGone : MEDIA_TEXT.imageGone}
      </p>
    )
  }
  if (error) {
    return <p className="text-xs text-destructive italic mb-1">{MEDIA_TEXT.unavailable}</p>
  }
  if (!objectUrl) {
    return <p className="text-xs text-muted-foreground italic mb-1">{MEDIA_TEXT.loading}</p>
  }
  if (show === 'image') {
    const thumbnail = (
      <img
        src={objectUrl}
        alt={MEDIA_TEXT.attachmentAlt}
        className="max-w-full h-auto max-h-[480px] rounded-lg border border-border mb-1"
      />
    )
    if (kind !== 'image') return thumbnail
    return (
      <>
        <button
          type="button"
          onClick={() => setViewing(true)}
          aria-label={thumbnailLabel(filename)}
          className="block cursor-zoom-in"
        >
          {thumbnail}
        </button>
        {viewing && (
          <MediaViewerDialog {...viewerFor(props, 'image', objectUrl)} onClose={() => setViewing(false)} />
        )}
      </>
    )
  }
  return (
    <audio
      controls
      src={objectUrl}
      data-testid="audio-attachment"
      className="max-w-[240px] mb-1"
    />
  )
}

/** SA-981 (design note §4.1): nothing is fetched until the click, which opens
 * the viewer; the viewer mints its link. */
function ViewButton(props: WithKind<ViewableKind>) {
  const { kind, filename, byteSize } = props
  const [viewing, setViewing] = useState(false)
  const Icon = KIND_ICON[kind]
  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setViewing(true)}
        className="text-xs text-primary flex items-center gap-1 min-w-0 max-w-full text-left"
      >
        <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
        {/* SA-1110: a sender's name with no spaces wraps here rather than widening the bubble; only the name is underlined. */}
        <span className="underline min-w-0 [overflow-wrap:anywhere]">{filename || kindLabel(kind)}</span>
        {byteSize != null && (
          <span className="text-muted-foreground shrink-0"> · {formatBytes(byteSize)}</span>
        )}
      </button>
      {viewing && (
        <MediaViewerDialog {...viewerFor(props, kind, null)} onClose={() => setViewing(false)} />
      )}
    </div>
  )
}
